import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Payment Method Update Form Component
const PaymentMethodUpdateForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create setup intent
      const { data } = await axios.post('/api/subscription/create-setup-intent');
      
      // Confirm card setup
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
        data.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Update payment method on backend
      await axios.post('/api/subscription/update-payment', {
        payment_method_id: setupIntent.payment_method
      });

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update payment method');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="border border-gray-300 rounded-md p-4">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      <div className="flex space-x-3">
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md font-medium"
        >
          {loading ? 'Updating...' : 'Update Payment Method'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Main Subscription Management Component
const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpdatePayment, setShowUpdatePayment] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSubscriptionData();
    fetchBillingHistory();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const { data } = await axios.get('/api/subscription/current');
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setMessage({ type: 'error', text: 'Failed to load subscription details' });
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const { data } = await axios.get('/api/subscription/billing-history');
      setBillingHistory(data.invoices || []);
    } catch (error) {
      console.error('Error fetching billing history:', error);
    }
  };

  const handleChangePlan = async (newPlan) => {
    if (window.confirm(`Change to ${newPlan} plan? You will be charged a prorated amount.`)) {
      setActionLoading(true);
      try {
        await axios.post('/api/subscription/change-plan', { new_plan: newPlan });
        setMessage({ type: 'success', text: `Successfully changed to ${newPlan} plan!` });
        setShowChangePlan(false);
        fetchSubscriptionData();
      } catch (error) {
        setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to change plan' });
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleCancelSubscription = async (immediate) => {
    setActionLoading(true);
    try {
      await axios.post('/api/subscription/cancel', { immediate });
      setMessage({ 
        type: 'success', 
        text: immediate ? 'Subscription cancelled immediately' : 'Subscription will cancel at end of billing period' 
      });
      setShowCancelModal(false);
      fetchSubscriptionData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to cancel subscription' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (window.confirm('Reactivate your subscription?')) {
      setActionLoading(true);
      try {
        await axios.post('/api/subscription/reactivate');
        setMessage({ type: 'success', text: 'Subscription reactivated successfully!' });
        fetchSubscriptionData();
      } catch (error) {
        setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to reactivate subscription' });
      } finally {
        setActionLoading(false);
      }
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      cancelled: 'bg-red-100 text-red-800',
      unpaid: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/independent/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Dashboard
              </button>
              <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Message Alert */}
        {message && (
          <div className={`mb-6 px-4 py-3 rounded-md ${
            message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            <div className="flex justify-between items-center">
              <span>{message.text}</span>
              <button onClick={() => setMessage(null)} className="text-xl">&times;</button>
            </div>
          </div>
        )}

        {/* Current Subscription */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Current Subscription</h2>
          </div>
          <div className="px-6 py-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-500">Plan</label>
                <p className="mt-1 text-lg font-semibold text-gray-900 capitalize">
                  {subscription?.subscription_plan} Plan
                </p>
                <p className="text-sm text-gray-600">
                  {subscription?.subscription_plan === 'monthly' ? '$14.99/month' : '$11.99/month (billed yearly)'}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <div className="mt-1">
                  {getStatusBadge(subscription?.subscription_status)}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Next Billing Date</label>
                <p className="mt-1 text-lg text-gray-900">
                  {subscription?.subscription_end_date ? formatDate(subscription.subscription_end_date) : 'N/A'}
                </p>
              </div>
              
              {subscription?.payment_method && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Payment Method</label>
                  <p className="mt-1 text-lg text-gray-900">
                    {subscription.payment_method.brand?.toUpperCase()} •••• {subscription.payment_method.last4}
                  </p>
                  <p className="text-sm text-gray-600">
                    Expires {subscription.payment_method.exp_month}/{subscription.payment_method.exp_year}
                  </p>
                </div>
              )}
              
              {subscription?.cancel_at_period_end && (
                <div className="md:col-span-2">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <p className="text-yellow-800">
                      ⚠️ Your subscription will cancel on {formatDate(subscription.subscription_end_date)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Manage Subscription</h2>
          </div>
          <div className="px-6 py-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setShowUpdatePayment(!showUpdatePayment)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-md font-medium"
              >
                Update Payment Method
              </button>
              
              <button
                onClick={() => setShowChangePlan(!showChangePlan)}
                disabled={subscription?.subscription_status === 'canceled' || subscription?.subscription_status === 'cancelled'}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-md font-medium"
              >
                Change Plan
              </button>
              
              {subscription?.cancel_at_period_end ? (
                <button
                  onClick={handleReactivate}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-md font-medium"
                >
                  Reactivate Subscription
                </button>
              ) : (
                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={subscription?.subscription_status === 'canceled' || subscription?.subscription_status === 'cancelled'}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-md font-medium"
                >
                  Cancel Subscription
                </button>
              )}
            </div>

            {/* Update Payment Method Form */}
            {showUpdatePayment && (
              <div className="mt-6 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Update Payment Method</h3>
                <Elements stripe={stripePromise}>
                  <PaymentMethodUpdateForm
                    onSuccess={() => {
                      setShowUpdatePayment(false);
                      setMessage({ type: 'success', text: 'Payment method updated successfully!' });
                      fetchSubscriptionData();
                    }}
                    onCancel={() => setShowUpdatePayment(false)}
                  />
                </Elements>
              </div>
            )}

            {/* Change Plan Options */}
            {showChangePlan && (
              <div className="mt-6 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Select New Plan</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`border-2 rounded-lg p-4 ${subscription?.subscription_plan === 'monthly' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                    <h4 className="text-lg font-semibold">Monthly Plan</h4>
                    <p className="text-2xl font-bold text-gray-900 mt-2">$14.99<span className="text-sm font-normal text-gray-600">/month</span></p>
                    <p className="text-sm text-gray-600 mt-2">Billed monthly</p>
                    {subscription?.subscription_plan !== 'monthly' && (
                      <button
                        onClick={() => handleChangePlan('monthly')}
                        disabled={actionLoading}
                        className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
                      >
                        Switch to Monthly
                      </button>
                    )}
                  </div>
                  
                  <div className={`border-2 rounded-lg p-4 ${subscription?.subscription_plan === 'yearly' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                    <h4 className="text-lg font-semibold">Yearly Plan</h4>
                    <p className="text-2xl font-bold text-gray-900 mt-2">$11.99<span className="text-sm font-normal text-gray-600">/month</span></p>
                    <p className="text-sm text-gray-600 mt-2">Billed annually ($143.88/year)</p>
                    <p className="text-sm text-green-600 font-medium">Save 20%</p>
                    {subscription?.subscription_plan !== 'yearly' && (
                      <button
                        onClick={() => handleChangePlan('yearly')}
                        disabled={actionLoading}
                        className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
                      >
                        Switch to Yearly
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Billing History */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Billing History</h2>
          </div>
          <div className="px-6 py-5">
            {billingHistory.length === 0 ? (
              <p className="text-gray-600">No billing history available</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {billingHistory.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(invoice.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${invoice.amount.toFixed(2)} {invoice.currency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            invoice.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {invoice.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {invoice.invoice_pdf && (
                            <a
                              href={invoice.invoice_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              Download PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Cancel Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Subscription</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to cancel your subscription? You can choose to cancel immediately or at the end of your billing period.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => handleCancelSubscription(false)}
                  disabled={actionLoading}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md font-medium"
                >
                  Cancel at End of Period
                </button>
                <button
                  onClick={() => handleCancelSubscription(true)}
                  disabled={actionLoading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium"
                >
                  Cancel Immediately
                </button>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium"
                >
                  Keep Subscription
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SubscriptionManagement;
